import {TimeZone} from '@hconnect/common/types'
import {
  QuickSelectionId as DefaultQuickSelectionId,
  timeRangeParamToDateRange
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {SelectionItem} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/types'
import {TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'

import {DEFAULT_TIME_RANGE} from '../consts'

type JanusQuickSelectionId =
  | 'lastMonth'
  | 'yearToMonthYTM'
  | 'lastYear'
  | 'currentMonth'
  | 'yearToDateYTD'

type QuickSelectionId = DefaultQuickSelectionId | JanusQuickSelectionId

export const getQuickSelectionItems = (
  translationFn: TFunction,
  timezone: TimeZone,
  now = new Date()
): SelectionItem<QuickSelectionId>[] => {
  const startOfToday: Moment = moment.utc(now).tz(timezone).startOf('day')
  const endOfToday: Moment = moment.utc(now).tz(timezone).startOf('minute')

  const startOfYesterday: Moment = startOfToday.clone().subtract(1, 'day')
  const endOfYesterday: Moment = startOfYesterday.clone().endOf('day')

  const currentMonth = startOfToday.clone().startOf('month').month()

  const quickSelectItems: SelectionItem<QuickSelectionId>[] = []

  quickSelectItems.push(
    {
      id: 'today',
      title: translationFn('quickSelection.today'),
      dates: [startOfToday.toDate(), endOfToday.toDate()]
    },
    {
      id: 'yesterday',
      title: translationFn('quickSelection.yesterday'),
      dates: [startOfYesterday.toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'last7days',
      title: translationFn('quickSelection.last7days'),
      dates: [startOfToday.clone().subtract(7, 'day').toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'currentMonth',
      title: translationFn('quickSelection.currentMonth'),
      dates: [startOfToday.clone().startOf('month').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastMonth',
      title: translationFn('quickSelection.lastMonth'),
      dates: [
        startOfToday.clone().subtract(1, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    }
  )

  if (currentMonth !== 0) {
    quickSelectItems.push({
      id: 'yearToMonthYTM',
      title: translationFn('quickSelection.yearToMonthYTM'),
      dates: [
        startOfToday.clone().startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    })
  }

  quickSelectItems.push(
    {
      id: 'last3months',
      title: translationFn('quickSelection.last3months'),
      dates: [
        startOfToday.clone().subtract(3, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    },
    {
      id: 'yearToDateYTD',
      title: translationFn('quickSelection.yearToDateYTD'),
      dates: [startOfToday.clone().startOf('year').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastYear',
      title: translationFn('quickSelection.lastYear'),
      dates: [
        startOfToday.clone().subtract(1, 'year').startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'year').endOf('year').toDate()
      ]
    }
  )

  return quickSelectItems
}

const getDefaultSelectionItem = (
  selectionItems: SelectionItem<QuickSelectionId>[],
  timezone: TimeZone
) => {
  const item = selectionItems.find((item) => item.id === DEFAULT_TIME_RANGE)

  if (!item) throw new Error(`Default selection item "${DEFAULT_TIME_RANGE}" not found`)

  const start = item.dates[0]
  const defaultEnd = moment.utc().tz(timezone).startOf('minute').toDate()
  const end = item.dates[1] ?? defaultEnd
  const quickSelectionId = item.id

  return [start, end, quickSelectionId] as const
}

type ParseTimeRangeParams = {
  timeRange: string
  timezone: TimeZone
  t: TFunction
}

export const parseTimeRangeParamToDateRange = ({timeRange, timezone, t}: ParseTimeRangeParams) => {
  const selectionItems = getQuickSelectionItems(t, timezone)
  const [defaultStart, defaultEnd, defaultQuickSelectionId] = getDefaultSelectionItem(
    selectionItems,
    timezone
  )
  const [start, end, quickSelectionId] = timeRangeParamToDateRange({timeRange, selectionItems})

  return {
    from: start ?? defaultStart,
    to: end ?? defaultEnd,
    quickSelectionId: quickSelectionId ?? defaultQuickSelectionId
  }
}
